/*
This file contains only logic used for the bottom menu
There should be no dependencies on react or hooks here
This will make it easier to test the functionalities
*/

import { IDashboardContext } from "../../contexts/dashboard-context";
import { isBackgroundColorPanelSupported } from "../bottom-background-menus/background-color-panel/menu-info";
import { isBackgroundPatternPanelSupported } from "../bottom-background-menus/background-pattern-panel/menu-info";
import { isBackgroundPanelSupported } from "../bottom-background-menus/menu-info";
import { isDesignsPanelSupported } from "../bottom-designs-menus/menu-info";
import { isTemplatesPanelSupported } from "../bottom-layouts-menu/menu-info";
import { isBottomRootMenuSupported } from "../bottom-menus/bottom-root-menu";
import { isPhotoEditingMenuSupported } from "../bottom-photo-menus/bottom-photo-editing/menu-info";
import { isBottomPhotoMenuSupported } from "../bottom-photo-menus/bottom-photo-menu/menu-info";
import { isPhotoEditingPanelSupported } from "../bottom-photo-menus/photo-editing-panel/menu-info";
import { isPhotoFiltersPanelSupported } from "../bottom-photo-menus/photo-filters-panel/menu-info";
import { isPhotoSelectionPanelSupported } from "../bottom-photo-menus/photo-selection-panel/menu-info";
import { isPhotoTransformSupported } from "../bottom-photo-menus/photo-transform-panel/menu-info";
import { isBottomTextMenuSupported } from "../bottom-text-menus/bottom-text-menu/menu-info";
import { isFontFamiliesPanelSupported } from "../bottom-text-menus/font-families-panel/menu-info";
import { isTextAlignmentPanelSupported } from "../bottom-text-menus/text-alignment-menu/menu-info";
import { isTextColorPanelSupported } from "../bottom-text-menus/text-color-panel/menu-info";
import { isTextSizePanelSupported } from "../bottom-text-menus/text-size-panel/menu-info";
import { isTextStylePanelSupported } from "../bottom-text-menus/text-style-panel/menu-info";
import { isTextValuePanelSupported } from "../bottom-text-menus/text-value-panel/menu-info";
import { isSymbolsPanelSupported } from "../bottom-menu-panels/symbols/menu-info";
import { BottomMenuIds, BottomMenuPaths } from "./paths";

function isPhotoRelatedBottomMenu(currentPath: string | undefined) {
	return (
		currentPath === BottomMenuPaths.PhotoEditing ||
		currentPath === BottomMenuPaths.PhotoBrightness ||
		currentPath === BottomMenuPaths.PhotoContrast ||
		currentPath === BottomMenuPaths.PhotoSaturation ||
		currentPath === BottomMenuPaths.PhotoFilters ||
		currentPath === BottomMenuPaths.PhotoTransform ||
		currentPath?.startsWith(`${BottomMenuPaths.Photo}/`) // All menus under photo
	);
}

/**
 * Check if this menu can be shown
 */
export function isSupportedMenu(
	menuId: string,
	zone: eXpress.core.IZone | undefined,
	page: eXpress.core.IPageZone | null | undefined,
	{
		main: dashboard,
		text: textDashboard,
	}: {
		main: IDashboardContext;
		text?: eXpress.TextZoneDashboard;
	},
): boolean {
	switch (menuId) {
		case BottomMenuIds.Root: {
			return isBottomRootMenuSupported();
		}
		case BottomMenuIds.Photo: {
			return isBottomPhotoMenuSupported(zone, dashboard, page);
		}
		case BottomMenuIds.PhotoSelection: {
			return isPhotoSelectionPanelSupported(page);
		}
		case BottomMenuIds.PhotoFilters: {
			return isPhotoFiltersPanelSupported(zone, dashboard);
		}
		case BottomMenuIds.PhotoEditing: {
			return isPhotoEditingMenuSupported(zone, dashboard);
		}
		case BottomMenuIds.PhotoTransform: {
			return isPhotoTransformSupported(zone, dashboard);
		}
		case BottomMenuIds.PhotoBrightness:
		case BottomMenuIds.PhotoContrast:
		case BottomMenuIds.PhotoSaturation: {
			return isPhotoEditingPanelSupported(zone);
		}
		case BottomMenuIds.Backgrounds: {
			return isBackgroundPanelSupported(page?.spread, dashboard);
		}
		case BottomMenuIds.BackgroundColor: {
			return isBackgroundColorPanelSupported(page?.spread, dashboard);
		}
		case BottomMenuIds.BackgroundPattern: {
			return isBackgroundPatternPanelSupported(page?.spread, dashboard);
		}
		case BottomMenuIds.Layouts: {
			return isTemplatesPanelSupported(page, dashboard);
		}
		case BottomMenuIds.Designs: {
			return isDesignsPanelSupported(page);
		}
		case BottomMenuIds.Symbols: {
			return isSymbolsPanelSupported(zone, dashboard);
		}
		case BottomMenuIds.TextColor:
		case BottomMenuIds.FontFamilies:
		case BottomMenuIds.Text:
		case BottomMenuIds.TextValue:
		case BottomMenuIds.TextSize:
		case BottomMenuIds.TextStyle:
		case BottomMenuIds.TextAlignment: {
			if (!textDashboard) {
				throw new Error(`Menu ${menuId} requires a textzone dashboard`);
			}
			switch (menuId) {
				case BottomMenuIds.TextColor: {
					return isTextColorPanelSupported(zone, textDashboard);
				}
				case BottomMenuIds.FontFamilies: {
					return isFontFamiliesPanelSupported(zone, textDashboard);
				}
				case BottomMenuIds.Text: {
					return isBottomTextMenuSupported(zone, page, textDashboard);
				}
				case BottomMenuIds.TextValue: {
					return isTextValuePanelSupported(zone, page?.definition, textDashboard);
				}
				case BottomMenuIds.TextSize: {
					return isTextSizePanelSupported(zone, textDashboard);
				}
				case BottomMenuIds.TextAlignment: {
					return isTextAlignmentPanelSupported(zone, page?.definition, textDashboard);
				}
				case BottomMenuIds.TextStyle:
					return isTextStylePanelSupported(zone, page?.definition, textDashboard);
				default:
					return true;
			}
		}
		default: {
			// Can we do an exaustiveness check here, so we get notified when a new menu is added
			return true; // show true, log message on console?
		}
	}
}

export function getDefaultPathForZone(
	currentPath: string | undefined,
	zone: eXpress.core.IZone | undefined,
	page: eXpress.core.IPageZone | null | undefined,
	dashboards: Parameters<typeof isSupportedMenu>[3],
	isTransformMode: boolean,
) {
	/**
	 * Check if this entire menu path can be shown
	 */
	function isSupportedPath(path: string) {
		return path
			.split("/")
			.every((pathPart) => isSupportedMenu(pathPart, zone, page, dashboards));
	}

	// If a photo zone is selected, we try to go to a photo menu (even if the opened menu can be used, example: templates)
	if (eXpress.core.isPhotoZone(zone)) {
		if (zone.photo?.photoSourceType === "qr" || zone.defaultPhotoSourceType === "qr") {
			if (dashboards.main.canEditQrZone(zone)) {
				return BottomMenuPaths.Qr;
			}
		}

		// For map zones, don't show photo menu's as default
		if (!zone.isZoneForPhotoSourceType("photo")) {
			return BottomMenuPaths.Root;
		}

		// If an empty photo zone is selected, open only the photo-selection
		if (zone.isEmpty && isSupportedPath(BottomMenuPaths.PhotoSelection)) {
			return BottomMenuPaths.PhotoSelection;
		}

		// Enter transform mode
		if (isTransformMode && isSupportedPath(BottomMenuPaths.PhotoTransform)) {
			return BottomMenuPaths.PhotoTransform;
		}

		// Leave transform mode
		if (!isTransformMode && currentPath === BottomMenuPaths.PhotoTransform) {
			if (isSupportedPath(BottomMenuPaths.Photo)) {
				return BottomMenuPaths.Photo;
			}
			return BottomMenuIds.Root;
		}

		// If a photo menu was already open try to keep the same menu open
		if (currentPath && isPhotoRelatedBottomMenu(currentPath) && isSupportedPath(currentPath)) {
			return currentPath;
		}

		// If we cannot keep the current menu open, show the root photo menu if possible
		if (isSupportedPath(BottomMenuPaths.Photo)) {
			return BottomMenuPaths.Photo;
		}
	}

	// If a text zone is selected, we try to go to a text menu (even if the opened menu can be used, example: templates)
	if (eXpress.core.isAnyTextZone(zone)) {
		// If TextMenu was already open try to reopen the same menu
		if (currentPath?.startsWith(`${BottomMenuPaths.Text}/`) && isSupportedPath(currentPath)) {
			return currentPath;
		}

		// If text-value is open, keep it open
		if (
			currentPath === BottomMenuPaths.TextValue &&
			isSupportedPath(BottomMenuPaths.TextValue)
		) {
			return BottomMenuPaths.TextValue;
		}

		// Remark We don't want to open text edit automatically in zone select
		if (isSupportedPath(BottomMenuPaths.Text)) {
			return BottomMenuPaths.Text;
		}
	}

	if (eXpress.core.isSymbolZone(zone)) {
		// If Symbols was already open try to reopen the same menu
		if (currentPath?.startsWith(BottomMenuPaths.Symbols) && isSupportedPath(currentPath)) {
			return currentPath;
		}
		// Try to open the symbols list menu
		if (isSupportedPath(BottomMenuPaths.SymbolsList)) {
			return BottomMenuPaths.SymbolsList;
		}
		// Open root symbol menu if not supported
		if (isSupportedPath(BottomMenuPaths.Symbols)) {
			return BottomMenuPaths.Symbols;
		}
	}

	// First try to keep the current menu open (like a Backgrounds / Templates Panel)
	if (currentPath !== undefined && isSupportedPath(currentPath)) {
		return currentPath;
	}

	// Try Root Menu
	if (isSupportedPath(BottomMenuPaths.Root)) {
		return BottomMenuPaths.Root;
	}

	// No menu.NoMenu
	return BottomMenuIds.None;
}
